import { type ReactElement, useState } from "react";
import { graphql, useMutation } from "react-relay";
import { useForm } from "react-hook-form";
import { ArrowIcon, Button, Input, Text } from "@olivahealth/oli-ui";
import { VALID_EMAIL_REGEXP } from "@olivahealth/constants";
import useTranslation from "../../../../hooks/useTranslation";
import { ForgotPasswordFormMutation as IMutation } from "./__generated__/ForgotPasswordFormMutation.graphql";

interface ForgotPasswordFormFields {
  email: string;
}

const ForgotPasswordFormMutation = graphql`
  mutation ForgotPasswordFormMutation($email: ID!) {
    resetPassword(email: $email) {
      success
    }
  }
`;

export default function ForgotPasswordForm({
  toggleAuthMethod,
}: {
  toggleAuthMethod: () => void;
}): ReactElement {
  const [resetPassword] = useMutation<IMutation>(ForgotPasswordFormMutation);
  const { register, handleSubmit, reset, formState } =
    useForm<ForgotPasswordFormFields>({
      mode: "onChange",
    });
  const { t } = useTranslation("auth", { keyPrefix: "forgotPasswordForm" });
  const [formError, setFormError] = useState<string>("");
  const [successEmail, setSuccessEmail] = useState<string | null>(null);

  const isButtonDisabled =
    !formState.isDirty || !formState.isValid || formState.isSubmitting;

  const onSubmit = handleSubmit(({ email }) => {
    resetPassword({
      variables: {
        email,
      },
      onCompleted: () => {
        setSuccessEmail(email);
        reset();
      },
      onError: (error) => {
        setFormError(error.message ?? t("errorMessage"));
      },
    });
  });

  if (successEmail) {
    return (
      <div className="animate animate-fade-in duration-1000 w-full">
        <Button
          variant="transparent"
          aria-label={t("goBackLinkAriaLabel")}
          type="button"
          size="xs"
          onClick={toggleAuthMethod}
        >
          <ArrowIcon direction="left" size={24} />
        </Button>
        <Text variant="h3" align="center" color="white" gutter="lg">
          {t("successTitle")}
        </Text>
        <Text gutter="lg" color="white" align="center">
          {t("successMessage", { email: successEmail })}
        </Text>
      </div>
    );
  }

  return (
    <form
      className="animate animate-fade-in duration-1000 w-full"
      onSubmit={onSubmit}
    >
      <Button
        variant="transparent"
        aria-label={t("goBackLinkAriaLabel")}
        type="button"
        size="xs"
        onClick={toggleAuthMethod}
      >
        <ArrowIcon direction="left" size={24} />
      </Button>
      <Text variant="h3" align="center" color="white" gutter="lg">
        {t("title")}
      </Text>
      <Text gutter="lg" color="white">
        {t("description")}
      </Text>
      <div className="flex flex-col gap-6">
        <Input
          id="email"
          register={register}
          label={t("email.label")}
          placeholder={t("email.placeholder")}
          type="email"
          validationOptions={{
            pattern: VALID_EMAIL_REGEXP,
          }}
          hasError={Boolean(formState.errors.email)}
          errorMessage={t("email.error")}
          variant="dark"
        />
        <Button variant="green" type="submit" disabled={isButtonDisabled}>
          {t("buttonLabel")}
        </Button>
        {formError !== "" && <Text color="error">{formError}</Text>}
      </div>
    </form>
  );
}
